import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, CheckCircle, XCircle, Coins, Swords, ArrowLeft } from "lucide-react";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";

export default function ContractOffers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [actionModal, setActionModal] = useState(null); // 'accept' or 'decline'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      const { data } = await api.get("/arena/contracts/offers");
      setOffers(data.offers);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!selectedOffer) return;

    setBusy(true);
    try {
      const { data } = await api.post(`/arena/contracts/${selectedOffer.id}/accept`);
      toast.success(data.message, { 
        description: `+${data.gold_received} gold received`,
        icon: <Coins size={16} /> 
      });
      setActionModal(null);
      setSelectedOffer(null);
      await loadOffers();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedOffer) return;

    setBusy(true);
    try {
      const { data } = await api.post(`/arena/contracts/${selectedOffer.id}/decline`);
      toast.success(data.message);
      setActionModal(null);
      setSelectedOffer(null);
      await loadOffers();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <PageHeader
          icon={Mail}
          title="Contract Offers"
          subtitle={`${offers.length} pending offer${offers.length !== 1 ? "s" : ""}`}
        />
      </div>

      {offers.length === 0 ? (
        <Empty label="No Contract Offers">
          <p className="text-sm text-zinc-400">
            You don&apos;t have any pending contract offers for your gladiators
          </p>
        </Empty>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className="border border-amber-900/30 bg-zinc-950 p-5 hover:border-amber-600/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail size={20} className="text-amber-400" />
                    <div>
                      <h3 className="font-cinzel text-lg text-amber-300">
                        {offer.circuit_name}
                      </h3>
                      <p className="text-xs text-zinc-500">
                        From {offer.editor_name}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Fighter
                      </div>
                      <div className="text-amber-300 font-cinzel">{offer.fighter_name}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Contract Length
                      </div>
                      <div className="text-zinc-200 flex items-center gap-1">
                        <Swords size={14} className="text-amber-400" />
                        {offer.num_fights} fight{offer.num_fights !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Payment Per Fight
                      </div>
                      <div className="text-zinc-200 flex items-center gap-1">
                        <Coins size={14} className="text-amber-400" />
                        {offer.payment_per_fight} gold
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                        Total Payment
                      </div>
                      <div className="text-emerald-400 font-semibold flex items-center gap-1">
                        <Coins size={14} />
                        {offer.total_payment} gold
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 text-xs text-zinc-500">
                    Offered {new Date(offer.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex flex-col gap-2 min-w-[120px]">
                  <button
                    onClick={() => {
                      setSelectedOffer(offer);
                      setActionModal("accept");
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white transition-colors flex items-center justify-center gap-2 text-sm font-cinzel"
                  >
                    <CheckCircle size={14} />
                    Accept
                  </button>
                  <button
                    onClick={() => {
                      setSelectedOffer(offer);
                      setActionModal("decline");
                    }}
                    className="px-4 py-2 bg-red-900/20 border border-red-700/40 text-red-300 hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2 text-sm font-cinzel"
                  >
                    <XCircle size={14} />
                    Decline
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Accept Modal */}
      {actionModal === "accept" && selectedOffer && (
        <Modal open onClose={() => setActionModal(null)} title="Accept Contract">
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Accept contract from <span className="text-amber-300 font-cinzel font-semibold">{selectedOffer.circuit_name}</span> for <span className="text-amber-300 font-cinzel font-semibold">{selectedOffer.fighter_name}</span>?
            </p>

            <div className="bg-emerald-950/20 border border-emerald-700/40 rounded p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Contract Length:</span>
                  <span className="text-zinc-200">{selectedOffer.num_fights} fight{selectedOffer.num_fights !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Payment Per Fight:</span>
                  <span className="text-zinc-200">{selectedOffer.payment_per_fight} gold</span>
                </div>
                <div className="flex justify-between border-t border-emerald-700/40 pt-2 mt-2">
                  <span className="text-emerald-300 font-semibold">Total Payment:</span>
                  <span className="text-emerald-300 font-semibold">{selectedOffer.total_payment} gold</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-950/20 border border-amber-700/40 rounded p-3">
              <p className="text-xs text-amber-300">
                ℹ️ You will receive the full payment upfront. Your fighter will be bound to this circuit for {selectedOffer.num_fights} fight{selectedOffer.num_fights !== 1 ? "s" : ""}.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setActionModal(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                className={btn() + " flex-1 bg-emerald-600 hover:bg-emerald-500"}
                disabled={busy}
              >
                {busy ? "Accepting..." : "Accept Contract"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Decline Modal */}
      {actionModal === "decline" && selectedOffer && (
        <Modal open onClose={() => setActionModal(null)} title="Decline Contract">
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Decline contract from <span className="text-amber-300 font-cinzel font-semibold">{selectedOffer.circuit_name}</span>?
            </p>

            <div className="bg-zinc-900/50 border border-zinc-800 rounded p-3">
              <p className="text-xs text-zinc-400">
                This will permanently decline this offer. The editor will be refunded their escrowed gold.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setActionModal(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700"}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={handleDecline}
                className={btn() + " flex-1 bg-red-600 hover:bg-red-500"}
                disabled={busy}
              >
                {busy ? "Declining..." : "Decline Offer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
